import { selectCurrentBatchId } from "~/features/autostorePicking/autostorePicking.slice";
import { hubs } from "~/lib/signalr";
import { StoreState } from "~/redux/reducers";
import { getCurrentPick } from "~/redux/selectors/autostoreSelectors";
import {
  AutostorePickingState,
  SignalRPickEventDto,
  ToteEventDto
} from "~/types/api";

import autostorePickingApi, { shapePickingState } from "./autostorePicking";

type PickingStateCacheEntry = {
  gridId: string;
  portId: number;
  cacheDataLoaded: Promise<unknown>;
  cacheEntryRemoved: Promise<unknown>;
  getCachedData: () => AutostorePickingState | undefined;
  replaceCachedData: (pickingState: AutostorePickingState) => void;
  refetch: () => void;
};

// Handles server -> client streaming updates via SignalR to update the cache when the appropriate messages are received.
// https://redux-toolkit.js.org/rtk-query/usage/streaming-updates
const streamPickingStateUpdates = async ({
  gridId,
  portId,
  cacheDataLoaded,
  cacheEntryRemoved,
  getCachedData,
  replaceCachedData,
  refetch
}: PickingStateCacheEntry) => {
  await cacheDataLoaded;

  const pickSubscription = hubs.pick.subscribe({
    next: (event: SignalRPickEventDto) => {
      const currentData = getCachedData();
      if (!currentData || currentData.batchId !== event.batchId) return;

      // selected events have the complete picking state included. Use that to update the cached state.
      if (event.eventType.toLowerCase() === "selected") {
        replaceCachedData(shapePickingState(event.pickingState));
        return;
      }

      // if our current batch includes the canceled pick, force a refetch of the cached query data.
      if (
        event.eventType.toLowerCase() === "canceled" &&
        getCurrentPick(currentData)?.pickId === event.pick.pickId
      ) {
        refetch();
      }
    }
  });

  // when totes are updates as On Workstation or PositionConfirmed, force a refetch of the cached query data.
  // not a critical event, but useful when PTL is triggering server-side state updates.
  const toteSubscription = hubs.tote.subscribe({
    next: (event: ToteEventDto) => {
      if (
        event.gridId === gridId &&
        event.portId === portId &&
        (event.eventType === "On Workstation" ||
          event.eventType === "Position Confirmed")
      ) {
        refetch();
      }
    }
  });

  await cacheEntryRemoved;
  pickSubscription.unsubscribe();
  toteSubscription.unsubscribe();
};

export const streamGetPickingStateV1Updates: typeof autostorePickingApi.endpoints.getPickingStateV1.Types.QueryDefinition.onCacheEntryAdded =
  (args, api) =>
    streamPickingStateUpdates({
      gridId: args.gridId,
      portId: args.portId,
      cacheDataLoaded: api.cacheDataLoaded,
      cacheEntryRemoved: api.cacheEntryRemoved,
      getCachedData: () => api.getCacheEntry().data,
      replaceCachedData: (pickingState) =>
        api.updateCachedData(() => pickingState),
      refetch: () =>
        void api.dispatch(
          autostorePickingApi.endpoints.getPickingStateV1.initiate(args, {
            forceRefetch: true,
            subscribe: false
          })
        )
    });

export const streamGetBatchPickingStateUpdates: typeof autostorePickingApi.endpoints.getBatchPickingState.Types.QueryDefinition.onCacheEntryAdded =
  (args, api) =>
    streamPickingStateUpdates({
      gridId: args.gridId,
      portId: args.portId,
      cacheDataLoaded: api.cacheDataLoaded,
      cacheEntryRemoved: api.cacheEntryRemoved,
      getCachedData: () => api.getCacheEntry().data,
      replaceCachedData: (pickingState) =>
        api.updateCachedData(() => pickingState),
      refetch: () => {
        const state = api.getState() as StoreState;
        if (selectCurrentBatchId(state) !== args.batchId) return;
        void api.dispatch(
          autostorePickingApi.endpoints.getBatchPickingState.initiate(args, {
            forceRefetch: true,
            subscribe: false
          })
        );
      }
    });
